<script setup lang="ts">
import axios from 'axios'
import ReportDraftsPanel from './ReportDraftsPanel.vue'
import ReportParametersDialog from './ReportParametersDialog.vue'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { getBlobErrorMessage, parseContentDispositionFilename, saveBlobAsFile } from '@/modules/documents-new/utils/fileHelpers'
import { generateReport, getReportTemplates } from '@/modules/reportTemplates/api'
import { SHARED_ANCHORS } from '@/modules/onboarding/scenarios/shared/anchors'
import type { ReportContext, ReportFilters, ReportFormat, ReportSource, ReportTemplate } from '@/modules/reportTemplates/api'

const props = withDefaults(defineProps<{
  objectType: string
  objectId?: string | number
  context?: ReportContext
  filters?: ReportFilters
  ids?: Array<string | number>
  selectionLabel?: string
  sort?: string | null
  variant?: 'ear' | 'inline' | 'toolbar'
  /** Сторона квадратной кнопки варианта `toolbar` — под высоту соседних кнопок тулбара */
  size?: number
}>(), {
  context: 'single',
  variant: 'inline',
  size: 38,
})

const parameterForm = ref<{ template: ReportTemplate; format: ReportFormat; source: ReportSource; preview: boolean } | null>(null)
const emit = defineEmits<{ saved: [] }>()
const draftsOpened = ref(false)
const feedback = useFeedbackStore()
const opened = ref(false)
const loading = ref(false)
const error = ref('')
const templates = ref<ReportTemplate[]>([])
const downloading = ref<string | null>(null)
const objectId = computed(() => props.context === 'single' ? props.objectId : undefined)
const disabled = computed(() => props.context === 'single' && (objectId.value == null || String(objectId.value).trim() === ''))
const labels: Record<ReportFormat, string> = { pdf: 'PDF', docx: 'Word', xlsx: 'Excel' }
let catalogRequest: AbortController | undefined
let downloadRequest: AbortController | undefined

async function loadTemplates() {
  catalogRequest?.abort()
  const controller = new AbortController()
  catalogRequest = controller
  loading.value = true
  error.value = ''
  templates.value = []
  try {
    const items = await getReportTemplates(props.objectType, props.context, objectId.value, controller.signal)
    if (!controller.signal.aborted)
      templates.value = items
  }
  catch (cause) {
    if (!controller.signal.aborted)
      error.value = getApiError(cause, 'Не удалось загрузить формы отчётов')
  }
  finally {
    if (catalogRequest === controller)
      loading.value = false
  }
}

async function download(template: ReportTemplate, format: ReportFormat, preview = false) {
  if (downloading.value)
    return

  const source: ReportSource = props.context === 'single'
    ? { type: props.objectType, id: objectId.value }
    : { type: props.objectType, ...(props.ids?.length ? { ids: [...props.ids] } : { filters: JSON.parse(JSON.stringify(props.filters || {})) }), ...(props.sort ? { sort: props.sort } : {}) }
  if (preview || template.parameters?.length) {
    parameterForm.value = { template, format, source, preview }
    opened.value = false
    return
  }
  const controller = new AbortController()
  downloadRequest = controller
  downloading.value = `${template.id}:${format}`

  try {
    const response = await generateReport(template.id, source, format, controller.signal)
    if (controller.signal.aborted)
      return
    const filename = parseContentDispositionFilename(response.headers['content-disposition']) || `${template.name}.${format}`
    saveBlobAsFile(response.data, filename)
  }
  catch (cause) {
    if (controller.signal.aborted)
      return
    let message = getApiError(cause, 'Не удалось сформировать отчёт')
    if (axios.isAxiosError(cause) && cause.response?.data instanceof Blob)
      message = (await getBlobErrorMessage(cause.response.data)) || message
    feedback.showToast({ type: 'error', message })
  }
  finally {
    if (downloadRequest === controller)
      downloading.value = null
  }
}

watch(opened, value => {
  if (value && !disabled.value)
    void loadTemplates()
})

watch(() => [props.objectType, props.objectId, props.context], () => {
  catalogRequest?.abort()
  downloadRequest?.abort()
  opened.value = false
  parameterForm.value = null
  templates.value = []
  error.value = ''
  downloading.value = null
})

onBeforeUnmount(() => {
  catalogRequest?.abort()
  downloadRequest?.abort()
})
</script>

<template>
  <VTooltip
    location="bottom"
    text="Формы и отчёты"
  >
    <template #activator="{ props: tooltipProps }">
      <span
        v-bind="tooltipProps"
        class="d-inline-flex"
      >
        <VMenu
          v-model="opened"
          location="bottom end"
          :close-on-content-click="false"
          content-class="entity-form-menu report-template-menu"
          :max-width="440"
        >
          <template #activator="{ props: menuProps }">
            <VBtn
              v-bind="menuProps"
              icon="ri-file-chart-line"
              :color="variant === 'ear' ? 'primary' : 'secondary'"
              :variant="variant === 'ear' ? undefined : variant === 'toolbar' ? 'outlined' : 'text'"
              :class="variant === 'ear' ? 'entity-form-action-btn' : 'flex-shrink-0'"
              :size="variant === 'toolbar' ? size : undefined"
              :rounded="variant === 'toolbar' ? 'sm' : undefined"
              :data-onboarding="variant === 'toolbar' ? SHARED_ANCHORS.toolbar.reports : SHARED_ANCHORS.ear.reports"
              :disabled="disabled"
              :loading="Boolean(downloading)"
              aria-label="Формы и отчёты"
            />
          </template>

          <VCard class="report-template-card" min-width="300">
            <VCardTitle class="text-subtitle-1">Формы и отчёты</VCardTitle>
            <VBtn class="mx-4 mb-2" size="small" variant="text" prepend-icon="ri-draft-line" @click="opened = false; draftsOpened = true">Мои черновики</VBtn>
            <div v-if="context === 'collection'" class="px-4 pb-3 text-body-2 text-medium-emphasis">
              {{ selectionLabel || 'Все доступные записи по текущим фильтрам, со всех страниц.' }}
            </div>
            <VProgressLinear v-if="loading" indeterminate color="primary" aria-label="Загрузка форм" />
            <div v-if="loading" class="pa-4 text-body-2">Загружаем доступные формы…</div>
            <div v-else-if="error" class="pa-4 text-body-2" role="alert">
              {{ error }}
              <VBtn class="mt-2" size="small" variant="text" @click="loadTemplates">Повторить</VBtn>
            </div>
            <div v-else-if="!templates.length" class="pa-4 text-body-2">Доступных форм пока нет.</div>
            <template v-else>
              <div v-for="template in templates" :key="template.id" class="report-template-item pa-4 pt-3">
                <div class="text-body-1 font-weight-medium">{{ template.name }}</div>
                <div class="text-body-2 text-medium-emphasis mt-1">{{ template.description }}</div>
                <details v-if="template.references?.length" class="text-body-2 mt-2">
                  <summary class="cursor-pointer">Основание и применение</summary>
                  <div v-for="reference in template.references" :key="reference.url" class="mt-2">
                    <a :href="reference.url" target="_blank" rel="noopener noreferrer">{{ reference.title }}</a>
                    <div class="text-medium-emphasis mt-1">{{ reference.note }}</div>
                  </div>
                </details>
                <div class="d-flex flex-wrap gap-2 mt-3">
                  <VBtn
                    size="small"
                    variant="text"
                    prepend-icon="ri-eye-line"
                    :disabled="Boolean(downloading)"
                    :aria-label="`${template.name}: предпросмотр`"
                    @click.stop="download(template, template.default_format, true)"
                  >Предпросмотр</VBtn>
                  <VBtn
                    v-for="format in template.formats"
                    :key="format"
                    size="small"
                    variant="tonal"
                    color="primary"
                    :disabled="Boolean(downloading)"
                    :loading="downloading === `${template.id}:${format}`"
                    :aria-label="`${template.name}: скачать ${labels[format]}`"
                    @click.stop="download(template, format)"
                  >
                    {{ labels[format] }}
                  </VBtn>
                </div>
              </div>
            </template>
          </VCard>
        </VMenu>
      </span>
    </template>
  </VTooltip>
  <ReportParametersDialog
    v-if="parameterForm"
    :template="parameterForm.template"
    :format="parameterForm.format"
    :source="parameterForm.source"
    :preview-on-open="parameterForm.preview"
    @close="parameterForm = null"
    @saved="emit('saved')"
  />
  <VDialog v-model="draftsOpened" :z-index="2600" max-width="860" scrollable>
    <VCard>
      <VCardTitle class="d-flex justify-space-between align-center">Мои документы<VBtn icon="ri-close-line" variant="text" aria-label="Закрыть документы" @click="draftsOpened = false" /></VCardTitle>
      <VCardText><ReportDraftsPanel v-if="draftsOpened" :object-type="objectType" :object-id="objectId" /></VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped>
.report-template-card {
  max-inline-size: calc(100vw - 24px);
  max-block-size: min(640px, 75vh);
  overflow-y: auto;
}

.report-template-item + .report-template-item {
  border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
